import { defineStore } from "pinia";
import { ref } from "vue";

export const useModulesStore = defineStore("modules", () => {
  const modules = ref([
    {
      id: "MS_RDPB",
      id_v2: "pb_solving",
      type: "skill",
      name: "Résolution de problèmes",
      result_wordings: {
        A: {
          questions: [
            "Pouvez-vous décrire une situation complexe que vous avez résolue avec succès ? Quelle a été votre démarche ?",
            "Comment abordez-vous généralement les problèmes difficiles ?",
            "Imaginez que vous découvriez une inefficacité dans un processus clé de notre entreprise. Comment aborderiez-vous ce problème ?",
          ],
          analysis:
            "Ce talent excelle dans la résolution de problèmes, analysant rapidement les situations et élaborant des solutions efficaces. Il sait mobiliser des ressources adaptées pour gérer des problématiques complexes, tout en restant concentré sur les résultats. Sa capacité à prendre des décisions éclairées renforce son rôle stratégique au sein de l’équipe. Cependant, il pourrait être tenté d’opter pour des solutions perfectionnistes ou trop élaborées dans des contextes nécessitant une réponse rapide.",
          advices:
            "Confiez à ce talent des problématiques complexes ou stratégiques nécessitant une résolution rapide et précise. Encouragez-le à partager ses techniques d’analyse avec l’équipe.",
        },
        B: {
          questions: [
            "Pouvez-vous me décrire un problème significatif que vous avez résolu au travail ? Quelle a été votre approche ?",
            "Quelles sont les premières étapes que vous prenez lorsque vous êtes confronté à un nouveau problème ?",
            "Si une tâche importante est bloquée par un obstacle imprévu, comment réagiriez-vous pour le surmonter ?",
          ],
          analysis:
            "Ce talent est efficace dans la résolution de problèmes courants, bien qu’il puisse rencontrer des limites dans des contextes particulièrement complexes ou inédits. Il sait néanmoins utiliser des approches structurées pour analyser et résoudre les situations. Un travail sur les techniques avancées pourrait enrichir ses compétences dans ce domaine.",
          advices:
            "Orientez ce talent vers des projets plus complexes pour l’aider à affiner ses compétences. Proposez des formations sur des outils analytiques ou des méthodologies avancées (ex. : Six Sigma).",
        },
        C: {
          questions: [
            "Pouvez-vous me parler d'un moment où vous avez eu à résoudre un problème au travail ? Comment l'avez-vous abordé ?",
            "Quelles sont vos méthodes pour analyser un problème et trouver des solutions ?",
            "Si vous aviez à améliorer un processus inefficace dans votre département, par où commenceriez-vous ?",
          ],
          analysis:
            "Ce talent montre une aptitude moyenne à la résolution de problèmes, ayant parfois du mal à structurer son raisonnement face à des défis complexes. Cela peut entraîner des hésitations ou des erreurs dans l’élaboration des solutions. Avec un accompagnement et des exercices ciblés, il pourrait améliorer son approche méthodique.",
          advices:
            "Fournissez une formation sur les fondamentaux de la résolution de problèmes, incluant des techniques comme le brainstorming structuré ou la méthode des 5 pourquoi. Offrez un retour constructif et valorisez ses efforts pour le motiver à affiner sa réflexion.",
        },
        D: {
          questions: [
            "Pouvez-vous me décrire une situation où vous avez eu du mal à résoudre un problème ? Qu'avez-vous appris de cette expérience ?",
            "Comment gérez-vous les situations où la solution à un problème n'est pas immédiatement évidente ?",
            "Si vous rencontriez un problème technique que vous ne savez pas résoudre, quelle serait votre démarche pour trouver une solution ?",
          ],
          analysis:
            "Ce talent rencontre des difficultés importantes à résoudre des problèmes de manière autonome, ce qui le pousse à demander fréquemment du soutien. Son raisonnement peut être parfois limité par un manque de confiance ou de méthodologie. Un coaching spécifique sur l’analyse et la prise de décision serait bénéfique.",
          advices:
            "Fournissez un accompagnement étroit avec des exemples concrets et des guides étape par étape pour résoudre des problèmes spécifiques. Intégrez-le à des projets collaboratifs pour qu’il bénéficie des méthodes et réflexions des autres membres de l’équipe.",
        },
        E: {
          questions: [
            "Pouvez-vous me parler d'un problème que vous n'avez pas pu résoudre. Comment avez-vous géré la situation ?",
            "Quand vous êtes face à un problème difficile, quelles sont vos stratégies pour essayer de le résoudre ?",
            "Imaginez que vous êtes confronté à un problème complexe. Quelles seraient les étapes que vous suivriez pour vous assurer de résoudre ce problème ?",
          ],
          analysis:
            "Ce talent éprouve de grandes difficultés à identifier et résoudre les problèmes de manière efficace. Il peut se sentir rapidement dépassé et manquer de solutions viables. Un accompagnement peut être souhaité pour renforcer sa capacité à structurer et appliquer des démarches de résolution.",
          advices:
            "Proposez des exercices guidés pour renforcer sa logique et sa méthodologie. Associez-le à un mentor ou un pair expérimenté pour l’aider à développer ses compétences dans des contextes réels. Mettez l’accent sur des problématiques simples et atteignables pour construire progressivement sa confiance.",
        },
        skill_description:
          "La résolution de problèmes comprend la capacité à <b>identifier</b> un problème et à mettre en œuvre une méthodologie pour le <b>résoudre</b>. C’est une compétence clé pour tous les postes qui requièrent de <b>l’autonomie</b> et de la <b>prise de décision</b>.",
        advantages: [
          {
            icon: "accurate",
            text: "Prendre des décisions <b>rationnelles</b> face à un problème",
          },
          {
            icon: "target",
            text: "Définir un plan visant à <b>atteindre</b> un objectif",
          },
          {
            icon: "glasses",
            text: "Être à l’aise pour effectuer des raisonnements <b>analytiques</b>",
          },
        ],
        short_description:
          "Raisonnement, logique, projection, la résolution de problèmes permet de surmonter les défis complexes avec des solutions adaptées. ",
        questions: [
          "Pouvez-vous décrire une situation complexe que vous avez rencontrée au travail et expliquer comment vous l'avez analysée et résolue ?",
          "Face à un problème pour lequel vous n'aviez pas de solution immédiate, comment avez-vous procédé pour trouver une solution ?",
          "Comment gérez-vous les situations où plusieurs problèmes se présentent simultanément ? Pouvez-vous donner un exemple de la manière dont vous avez priorisé et traité ces problèmes ?",
        ],
        questions_info:
          "Ces questions sont conçues pour encourager les talents à <b>verbaliser</b> leurs <b>stratégies</b> pour identifier, analyser et résoudre des problèmes de manière efficace.",
      },
    },
    {
      id: "MS_GDSS",
      id_v2: "stress_manage",
      type: "skill",
      name: "Gestion du stress",
      result_wordings: {
        A: {
          questions: [
            "Pouvez-vous me donner un exemple de situation particulièrement stressante que vous avez bien gérée ? Comment avez-vous maintenu votre calme ?",
            "Quelles techniques utilisez-vous pour rester serein sous pression ?",
            "Imaginez que vous devez gérer plusieurs projets urgents en même temps. Comment vous vous y prendriez pour prioriser et gérer votre stress ?",
          ],
          analysis:
            "Ce talent démontre une excellente capacité à gérer le stress, restant calme et efficace même sous une forte pression. Il est capable de transformer des situations stressantes en opportunités de performance, ce qui en fait un atout dans des environnements exigeants. Sa résilience inspire ses collègues et renforce la stabilité de l’équipe. Cependant, cette excellence peut parfois entraîner une surcharge de travail si le talent est perçu comme toujours disponible pour gérer des projets supplémentaires.",
          advices:
            "Encouragez-le à partager ses pratiques avec l’équipe et veillez à équilibrer sa charge de travail pour éviter une surcharge. Favorisez des rôles où ses compétences en planification et organisation seront pleinement exploitées, tout en intégrant une flexibilité pour gérer des imprévus.",
        },
        B: {
          questions: [
            "Pouvez-vous me décrire une fois où vous avez dû gérer un stress important au travail ? Quelle a été votre approche ?",
            "Comment vous préparez-vous mentalement pour des situations que vous savez stressantes ?",
            "Si un client important était mécontent et vous exprimait son mécontentement, comment géreriez-vous cette situation stressante ?",
          ],
          analysis:
            "Ce talent gère bien le stress dans la majorité des cas, bien qu’il puisse être légèrement impacté dans des contextes exceptionnellement tendus. Il sait identifier les sources de stress et mobiliser des ressources personnelles pour maintenir son équilibre. Une préparation ciblée pourrait encore améliorer sa tolérance face à des périodes prolongées de pression.",
          advices:
            "Proposez des outils de priorisation avancés (ex. Kanban ou Eisenhower Matrix) pour renforcer ses réflexes en situation de stress. Valorisez ses réussites dans des périodes exigeantes pour renforcer sa confiance dans ces contextes.",
        },
        C: {
          questions: [
            "Pouvez-vous me parler d’un moment où vous avez ressenti du stress au travail et comment vous l'avez géré.",
            "Quelles sont vos stratégies pour gérer des périodes de stress modéré au travail ?",
            "Si vous receviez une tâche très difficile avec un délai serré, comment géreriez-vous votre stress pour l'accomplir ?",
          ],
          analysis:
            "Ce talent montre une gestion du stress acceptable, mais il peut être affecté par des situations intenses ou imprévues. Cela peut entraîner une baisse temporaire de sa performance ou de son efficacité. Avec des techniques adaptées, il pourrait mieux anticiper et gérer les pics de tension.",
          advices:
            "Fournissez des formations sur la planification et les outils de gestion de projets simples (ex. To-Do Lists structurées). Proposez un accompagnement ponctuel avec des feedbacks sur ses livrables pour renforcer ses bonnes pratiques.",
        },
        D: {
          questions: [
            "Pouvez-vous me décrire une situation où vous avez trouvé le stress difficile à gérer ? Comment avez-vous fait face ?",
            "Comment essayez-vous de réduire votre stress lorsqu'il devient trop intense ?",
            "Si une équipe sous votre supervision était stressée et performait mal, quelles actions prendriez-vous pour améliorer la situation ?",
          ],
          analysis:
            "Ce talent éprouve des difficultés à gérer le stress, ce qui impacte négativement ses performances dans des environnements exigeants. Il peut se replier sur lui-même ou perdre en productivité lorsqu’il est confronté à des échéances ou des charges importantes. Un accompagnement spécifique sur la gestion émotionnelle pourrait améliorer sa tolérance au stress.",
          advices:
            "Mettez en place un suivi avec des objectifs clairs et limités dans le temps. Proposez des outils visuels comme des tableaux de planification et introduisez des check-ins pour renforcer sa structuration et son organisation.",
        },
        E: {
          questions: [
            "Pouvez-vous me parler d'une fois où vous avez été submergé par le stress ? Quelles ont été vos réactions et que feriez-vous différemment aujourd'hui ?",
            "Quelles techniques avez-vous essayé pour gérer votre stress, et lesquelles ont fonctionné ou non ?",
            "Si vous deviez faire face à une situation de crise imprévue au travail, comment tenteriez-vous de gérer votre stress ?",
          ],
          analysis:
            "Ce talent rencontre de grandes difficultés à gérer des situations stressantes, ce qui affecte gravement sa stabilité et sa productivité. Il peut être sujet à des réactions négatives ou à un épuisement rapide. Un travail  sur les mécanismes de gestion du stress peut sembler important pour l’aider à retrouver un équilibre.",
          advices:
            "Proposez un accompagnement avec des étapes progressives pour apprendre à prioriser et anticiper. Offrez un mentorat ou un soutien managérial pour l’aider à développer des routines simples et à regagner confiance dans sa capacité à gérer ses échéances.",
        },
        skill_description:
          "Le stress en milieu professionnel peut revêtir une multitude de formes différentes, mais se caractérise toujours par une mise en <b>tension</b> de l’individu. La gestion du stress permet aux individus de faire face à ces situations en conservant la <b>pleine possession de leurs moyens</b>.",
        advantages: [
          {
            icon: "increase",
            text: "Des performances <b>constantes</b>, voire <b>améliorées</b> dans un contexte stressant",
          },
          {
            icon: "folder",
            text: "Une bonne capacité à travailler dans des environnements où les <b>ressources</b> sont <b>limitées</b>",
          },
          {
            icon: "flash",
            text: "Une <b>réception</b> plus <b>positive</b> des périodes intenses de travail",
          },
        ],
        short_description:
          "Équilibre, résilience, sérénité, la gestion du stress permet de maintenir un niveau de performance constant pour exceller sous pression.",
        questions: [
          "Quelles techniques ou méthodes utilisez-vous pour gérer efficacement le stress dans un environnement professionnel ?",
          "Pouvez-vous me raconter une période où vous avez été sous un stress important au travail ? Comment avez-vous géré cette pression tout en maintenant la qualité de votre travail ?",
          "Comment réagissez-vous lorsque vous êtes confronté à des échéances courtes ou à des exigences de travail élevées ? Avez-vous un exemple précis à partager ?",
        ],
        questions_info:
          "Ces questions vont vous permettre de mieux cerner la capacité de ce profil à gérer son stress <b>sans compromettre</b> la qualité de son travail.",
      },
    },
    {
      id: "MS_ADPT",
      id_v2: "adaptability",
      type: "skill",
      name: "Adaptabilité",
      result_wordings: {
        A: {
          questions: [
            "Pouvez-vous me parler d'une situation récente où vous avez dû vous adapter rapidement à un changement imprévu ? Comment avez-vous géré cela ?",
            "Comment abordez-vous les nouvelles technologies ou méthodes de travail dans votre secteur ?",
            "Imaginez que les procédures de votre travail évoluent. Comment réagiriez-vous et comment procéderiez-vous pour vous adapter ?",
          ],
          analysis:
            "Ce talent excelle dans l’adaptabilité, s’ajustant rapidement et efficacement dans des environnements en évolution constante. C’est un pilier dans les situations dynamiques. Cependant, une adaptabilité exceptionnelle peut parfois conduire à une surcharge ou à un manque de focus si le talent est sollicité de manière excessive pour gérer tous les imprévus.",
          advices:
            "Modérez les attentes envers ce talent pour éviter qu’il devienne un 'pompiériste universel', toujours mobilisé en cas de problème, au détriment de son efficacité ou de son bien-être. Assignez-lui des projets stratégiques où son adaptabilité sera un atout et valorisez ses réussites pour encourager la culture d’agilité sans générer de surcharge.",
        },
        B: {
          questions: [
            "Pouvez-vous me décrire une fois où vous avez dû changer votre façon de travailler en raison d'une nouvelle politique ou d'une nouvelle direction dans votre précédent emploi ?",
            "Comment gérez-vous les situations où vous devez acquérir rapidement de nouvelles compétences ?",
            "Si vous deviez changer d'équipe en milieu de projet, comment vous assureriez-vous que la transition soit aussi fluide que possible ?",
          ],
          analysis:
            "Ce talent montre une bonne adaptabilité, mais pourrait être davantage mis au défi dans des situations de changements rapides pour maximiser son potentiel.	Il reste néanmoins capable d’ajuster ses méthodes et son comportement pour répondre aux exigences du contexte. ",
          advices:
            "Orientez ce talent vers des missions dynamiques pour renforcer sa capacité à s’ajuster rapidement. Proposez des exercices pratiques ou simulations pour développer sa flexibilité.",
        },
        C: {
          questions: [
            "Pouvez-vous me parler d'un moment où vous avez dû vous adapter à un changement que vous n'aviez pas anticipé ? Comment cela s'est-il passé ?",
            "Comment réagissez-vous généralement aux changements soudains dans votre environnement de travail ?",
            "Si votre supérieur vous demandait de modifier votre méthode de travail du jour au lendemain, comment aborderiez-vous cette demande ?",
          ],
          analysis:
            "Ce talent affiche une adaptabilité modérée, mais montre parfois une difficulté à sortir de sa zone de confort, ce qui peut freiner son efficacité dans des contextes très imprévisibles. Avec un accompagnement adapté, il peut développer une plus grande tolérance aux situations inhabituelles.",
          advices:
            "Encouragez-le à participer à des projets variés et fournissez-lui des retours pour renforcer sa confiance en son adaptabilité.",
        },
        D: {
          questions: [
            "Pouvez-vous me décrire une situation où un changement imprévu vous a mis au défi ? Qu'avez-vous appris de cette expérience ?",
            "Quelle est votre stratégie pour faire face à des changements soudains dans votre travail ?",
            "Si vous deviez assumer un rôle complètement différent pendant quelques semaines, comment vous y prépareriez-vous ?",
          ],
          analysis:
            "Ce talent rencontre des difficultés à s’adapter à des changements fréquents ou importants, ce qui peut limiter sa contribution dans des contextes dynamiques. Il peut montrer des craintes à l’idée de modifier ses habitudes ou ses processus, ce qui limite sa flexibilité. ",
          advices:
            "Offrez un accompagnement lorsqu’il y a des contextes changeants. Valorisez ses efforts pour renforcer sa confiance face au changement.",
        },
        E: {
          questions: [
            "Pouvez-vous me parler d’une fois où vous avez eu du mal à vous adapter à un changement ? Quelles actions avez-vous prises pour essayer de vous ajuster ?",
            "Quelles sont vos méthodes pour gérer les changements qui vous mettent mal à l'aise ?",
            "Si vous étiez confronté à un changement de direction de l'entreprise qui affecte directement votre travail, comment réagiriez-vous ?",
          ],
          analysis:
            "Ce talent éprouve de grandes difficultés face aux changements, se montrant souvent dépassé par des situations imprévues. Cela affecte négativement sa performance et sa capacité à collaborer efficacement avec des équipes en transformation.",
          advices:
            "Proposez un accompagnement visant à développer des stratégies simples et concrètes pour aborder les changements. Valorisez ses efforts et succès valorisées pour restaurer sa confiance en sa capacité d’adaptation.",
        },
        skill_description:
          "L’adaptabilité, que l’on appelle en psychologie <b>flexibilité cognitive</b>, peut être définie comme la capacité de notre cerveau à adapter notre conduite et notre pensée à des situations nouvelles, changeantes ou inattendues.",
        advantages: [
          {
            icon: "time",
            text: "Une <b>adaptation rapide</b> aux changements et aux nouveautés de l’environnement",
          },
          {
            icon: "eye",
            text: "Une capacité à aborder les problèmes avec un <b>nouveau regard</b>",
          },
          {
            icon: "flag",
            text: "Une <b>tolérance accrue</b> aux imprévus et aux changements de cap",
          },
        ],
        short_description:
          "Flexibilité, réactivité, anticipation, l’adaptabilité permet d’évoluer avec aisance dans un environnement changeant.",
        questions: [
          "Comment réagissez-vous face à un changement soudain dans votre environnement de travail ?",
          "Face à une situation où les directives initiales ont été modifiées en cours de route, comment avez-vous réajusté votre stratégie pour atteindre les objectifs fixés ?",
          "Pouvez-vous partager une expérience où votre capacité d'adaptation a été cruciale pour le succès du projet ?",
        ],
        questions_info:
          "Ces questions vont vous permettre de prendre du recul sur la capacité du profil à s’adapter de manière <b>efficace</b> et <b>appropriée</b>.",
      },
    },
    {
      id: "active_listen",
      type: "skill",
      name: "Ecoute active",
      result_wordings: {
        A: {
          questions: [
            "Pouvez-vous me donner un exemple d'une situation où vous avez pu résoudre un conflit en écoutant attentivement les préoccupations de toutes les parties ?",
            "Comment adaptez-vous votre manière d'écouter lorsque vous travaillez avec des personnes de différents horizons ou ayant des perspectives différentes ?",
            "Imaginez que vous travaillez avec une équipe internationale. Comment utiliseriez-vous vos compétences en écoute active pour vous assurer que tout le monde se sente compris, malgré les différences culturelles et linguistiques ?",
          ],
          analysis:
            "Ce talent excelle dans l’écoute active, captant avec précision les messages verbaux et non verbaux de ses interlocuteurs. Il sait reformuler pour valider sa compréhension, créant ainsi un climat de confiance et une communication fluide. Cette compétence en fait un médiateur naturel dans des environnements collaboratifs.",
          advices:
            "Assignez à ce talent des rôles nécessitant de la médiation ou des échanges fréquents avec des parties prenantes variées. Encouragez-le à partager ses bonnes pratiques pour inspirer ses collègues à améliorer leur écoute.",
        },
        B: {
          questions: [
            "Pouvez-vous me décrire une fois où votre capacité à écouter attentivement a amélioré votre collaboration avec un collègue ou un client ?",
            "Comment faites-vous pour montrer que vous comprenez et prenez en compte les préoccupations des autres, même lorsque vous n'êtes pas d'accord avec eux ?",
            "Si un collègue venait vous voir avec un problème complexe, comment vous assureriez-vous que vous comprenez bien son point de vue avant de proposer une solution ?",
          ],
          analysis:
            "Ce talent démontre une bonne capacité d’écoute active, bien qu’il puisse parfois manquer de vigilance dans des situations complexes ou émotionnellement chargées. Sa capacité à prendre en compte différents points de vue reste néanmoins un atout dans les échanges quotidiens.",
          advices:
            "Orientez ce talent vers des exercices avancés de reformulation et de prise de perspective, notamment dans des situations complexes. Proposez-lui des retours réguliers pour renforcer sa vigilance dans des échanges plus techniques ou émotionnels.",
        },
        C: {
          questions: [
            "Pouvez-vous me parler d'une situation où il a été important pour vous de bien écouter les autres avant de prendre une décision ? Comment cela a-t-il influencé votre action ?",
            "Comment gérez-vous les situations où vous devez prêter attention à plusieurs personnes en même temps, tout en restant attentif à leurs besoins ?",
            "Si un membre de votre équipe avait du mal à exprimer ses idées, comment l’aideriez-vous à se sentir écouté et compris ?",
          ],
          analysis:
            "Ce talent montre une écoute fonctionnelle, mais son attention peut être irrégulière selon les contextes. Cela peut limiter la profondeur de ses interactions et son aptitude à résoudre efficacement les malentendus. Une meilleure maîtrise des techniques d’écoute pourrait accroître son impact relationnel.",
          advices:
            "Recommandez des ateliers spécifiques sur l’écoute active, incluant des mises en situation pratiques. Incitez-le à utiliser des techniques comme les questions ouvertes pour approfondir la compréhension de ses interlocuteurs.",
        },
        D: {
          questions: [
            "Pouvez-vous me décrire une fois où vous avez réalisé après coup que vous n'aviez pas suffisamment écouté un collègue ou un client ? Qu'avez-vous appris de cette expérience ?",
            "Comment gérez-vous les situations où vous avez du mal à rester concentré pendant une conversation longue ou complexe ?",
            "Si quelqu’un vous reprochait de ne pas avoir écouté attentivement ses idées, comment réagiriez-vous et que feriez-vous pour y remédier ?",
          ],
          analysis:
            "Ce talent a du mal à maintenir une écoute attentive et adaptée, ce qui peut entraver la qualité de ses relations professionnelles. Il peut également manquer d'empathie dans ses réponses, ce qui nuit à sa capacité à collaborer efficacement.",
          advices:
            "Proposez un accompagnement individuel axé sur les fondamentaux de l’écoute et l’intelligence émotionnelle. Intégrez-le à des situations collaboratives où des retours constructifs sur ses interactions pourront renforcer ses compétences.",
        },
        E: {
          questions: [
            "Pouvez-vous me parler d’un moment où vous avez eu des difficultés à écouter les autres dans une situation de stress ou de pression ? Comment avez-vous réagi ?",
            "Comment faites-vous face aux situations où vous avez du mal à rester attentif aux autres ? Quelles stratégies utilisez-vous pour rester concentré ?",
            "Si vous deviez participer à une réunion où vous n'êtes pas très à l'aise avec le sujet, comment essayeriez-vous de vous impliquer dans l’écoute des autres pour rester productif ?",
          ],
          analysis:
            "Ce talent éprouve des difficultés importantes à capter et comprendre les besoins de ses interlocuteurs. Cela peut générer des incompréhensions récurrentes et limiter son intégration dans des projets collaboratifs. Un travail intensif sur les bases de l’écoute est indispensable.",
          advices:
            "Offrez des opportunités de discussions structurées où il pourra observer et pratiquer des techniques d’écoute active. Associez-le à un mentor pour l’accompagner dans l’amélioration de sa sensibilité aux signaux verbaux et non verbaux.",
        },
        skill_description:
          "L’écoute active-empathique consiste à capter et comprendre les messages verbaux et non verbaux, tout en répondant de manière adaptée aux besoins de l’interlocuteur.",
        advantages: [
          {
            icon: "board-arrow",
            text: "Renforce la qualité des interactions et la compréhension mutuelle.",
          },
          {
            icon: "switch_icon_V2",
            text: "Facilite la résolution des conflits et améliore la coopération.",
          },
          {
            icon: "sun",
            text: "Contribue à un climat de travail serein et productif.",
          },
        ],
        short_description:
          "Attention, compréhension, réceptivité, l'écoute active permet de capter et comprendre les messages d'autrui.",
      },
    },
    {
      id: "auto_efficient",
      type: "skill",
      name: "Confiance en ses capacités",
      result_wordings: {
        A: {
          questions: [
            "Pouvez-vous me parler d'un projet complexe que vous avez mené avec succès ? Quelles étaient vos principales stratégies pour surmonter les défis ?",
            "Comment abordez-vous les situations où vous devez convaincre une équipe ou un client que votre solution est la meilleure ?",
            "Imaginez que vous deviez prendre une décision importante avec peu de temps pour réfléchir. Que feriez-vous ?",
          ],
          analysis:
            "Ce talent démontre une forte confiance en ses capacités, ce qui lui permet de prendre des décisions efficaces et d’explorer des approches innovantes. Toutefois, une confiance excessive pourrait limiter sa prise en compte des retours critiques ou nuire à son intégration dans une équipe.",
          advices:
            "Encouragez ce talent à partager ses réussites tout en cultivant une écoute active des retours. Assignez-lui des responsabilités dans des projets complexes pour capitaliser sur sa confiance tout en favorisant une approche collaborative.",
        },
        B: {
          questions: [
            "Pouvez-vous me décrire une situation où vous avez dû faire preuve d’assurance malgré des incertitudes importantes ? Comment avez-vous réussi à maintenir votre confiance en vous ?",
            "Quand vous êtes confronté à des tâches nouvelles ou inconnues, comment utilisez-vous votre confiance en vos compétences pour y faire face ?",
            "Si vous deviez gérer une situation dans laquelle vos compétences étaient remises en question, comment réagiriez-vous pour affirmer vos capacités ?",
          ],
          analysis:
            "Ce talent montre une confiance équilibrée, bien qu’il puisse hésiter dans des environnements très incertains. Ces doutes ponctuels n’affectent pas de manière significative sa capacité à accomplir ses missions.",
          advices:
            "Proposez-lui des projets légèrement en dehors de sa zone de confort pour renforcer sa résilience. Valorisez ses réussites pour consolider sa confiance dans des contextes exigeants.",
        },
        C: {
          questions: [
            "Pouvez-vous me parler d'une situation où vous avez dû sortir de votre zone de confort ? Comment avez-vous renforcé votre confiance en vous dans ce contexte ?",
            "Comment gérez-vous les situations où vous ne vous sentez pas immédiatement à l'aise avec une tâche ou une responsabilité ?",
            "Si vous deviez travailler sur une mission pour laquelle vous n’avez pas toutes les compétences, comment vous prépareriez-vous pour réussir ?",
          ],
          analysis:
            "Ce talent exprime parfois un manque de confiance, ce qui peut limiter sa prise d’initiative ou son impact dans des contextes nécessitant un leadership affirmé. Cette prudence peut être surmontée par une reconnaissance active de ses succès.",
          advices:
            "Fournissez des retours réguliers et valorisez ses contributions. Orientez-le vers un mentorat ou des ateliers spécifiques pour renforcer sa confiance en ses capacités.",
        },
        D: {
          questions: [
            "Pouvez-vous me parler d’un moment où vous avez douté de vos compétences pour accomplir une tâche ? Comment avez-vous surmonté ces doutes ?",
            "Comment réagissez-vous lorsque vous sentez que vous n’êtes pas le plus qualifié pour une tâche particulière ? Comment gérez-vous cette situation ?",
            "Si vous deviez entreprendre un projet pour lequel vous n’avez pas beaucoup d'expérience, comment travailleriez-vous pour développer votre confiance en vous-même ?",
          ],
          analysis:
            "Ce talent montre des difficultés notables à croire en ses compétences, ce qui limite son engagement et peut nuire à sa crédibilité perçue. Ce manque de confiance est souvent renforcé par des échecs passés ou un manque de validation.",
          advices:
            "Mettez l’accent sur des tâches atteignables et valorisez chaque progrès pour reconstruire sa confiance progressivement. Offrez-lui un encadrement bienveillant et des retours pour renforcer son estime de soi.",
        },
        E: {
          questions: [
            "Pouvez-vous me décrire une situation où vous avez eu l'impression de ne pas être à la hauteur des attentes ? Comment avez-vous réagi ?",
            "Comment faites-vous face aux moments où vous doutez fortement de vos capacités pour accomplir une tâche importante ?",
            "Si vous deviez prendre une décision dans un domaine où vous ne vous sentez pas compétent, comment approcheriez-vous cette situation pour avancer ?",
          ],
          analysis:
            "Ce talent doute fortement de ses compétences, ce qui peut affecter son autonomie et sa productivité. Il peut éviter les responsabilités ou se montrer très hésitant.",
          advices:
            "Proposez-lui des situations valorisantes où il peut réussir de manière encadrée. Orientez-le vers un mentor qui pourra l’accompagner et l’aider à développer une meilleure perception de ses compétences.",
        },
        skill_description:
          "La confiance en ses compétences permet à un talent de croire en ses aptitudes à accomplir efficacement ses missions.",
        advantages: [
          {
            icon: "network",
            text: "Accélère la prise de décision et la résolution de problèmes.",
          },
          {
            icon: "brush",
            text: "Encourage l’innovation et la créativité.",
          },
          {
            icon: "rond-check",
            text: "Renforce la crédibilité et l’influence au sein des équipes.",
          },
        ],
        short_description:
          "Assurance, reconnaissance de sa valeur, la confiance en ses compétences permet de s'affirmer professionnellement.",
      },
    },
    {
      id: "decisions",
      type: "skill",
      name: "Prise de décisions",
      result_wordings: {
        A: {
          questions: [
            "Pouvez-vous me parler d'une situation où vous avez dû prendre une décision critique rapidement ? Quelles informations avez-vous utilisées pour vous assurer que c'était la meilleure décision ?",
            "Comment intégrez-vous les retours d’équipe et les données dans votre processus de prise de décision, surtout dans des situations complexes ou incertaines ?",
            "Imaginez que vous avez plusieurs options de décision pour un projet important. Comment évaluez-vous les risques et bénéfices de chaque option avant de prendre votre décision finale ?",
          ],
          analysis:
            "Ce talent excelle dans la prise de décision, équilibrant efficacement les objectifs à court terme et les impacts à long terme. Il analyse rapidement les options et choisit des solutions stratégiques, même sous pression. Cependant, un excès d’analyse pourrait freiner les décisions dans des contextes où une action rapide est nécessaire.",
          advices:
            "Orientez ce talent vers des missions où des décisions stratégiques complexes sont nécessaires. Encouragez-le à maintenir un équilibre entre analyse et action pour éviter des retards dans des contextes urgents.",
        },
        B: {
          questions: [
            "Pouvez-vous me décrire une situation où vous avez pris une décision après avoir analysé plusieurs alternatives ? Comment avez-vous pesé les différentes options ?",
            "Comment gérez-vous les décisions difficiles lorsque les informations disponibles sont incomplètes ou contradictoires ?",
            "Si vous deviez prendre une décision ayant un impact à long terme sur votre équipe ou votre entreprise, comment vous assureriez-vous de choisir la meilleure approche ?",
          ],
          analysis:
            "Ce talent démontre de solides compétences en prise de décision, particulièrement sur des problématiques à court terme. Cependant, il peut avoir tendance à minimiser les impacts à long terme, notamment dans des situations incertaines.",
          advices:
            "Fournissez des exercices ou simulations axés sur l’anticipation des conséquences à long terme pour enrichir ses réflexes stratégiques. Valorisez ses décisions pertinentes pour renforcer sa confiance dans des contextes plus ambitieux.",
        },
        C: {
          questions: [
            "Pouvez-vous me parler d'une décision que vous avez dû prendre dans un contexte incertain ? Comment avez-vous géré l'incertitude et quelle a été l'issue de votre décision ?",
            "Comment équilibrez-vous la rapidité et la prudence dans votre prise de décision, surtout sous pression ?",
            "Si vous deviez faire face à un choix où chaque option présente des avantages et des inconvénients clairs, comment choisiriez-vous la meilleure voie à suivre ?",
          ],
          analysis:
            "Ce talent prend des décisions fonctionnelles, mais montre des difficultés à évaluer ou intégrer pleinement les impacts à long terme. Cela peut entraîner des choix impulsifs ou mal anticipés.",
          advices:
            "Incitez ce talent à documenter ses réflexions avant de prendre une décision pour clarifier ses idées. Proposez des ateliers sur la prise de décision structurée, incluant l’utilisation de matrices décisionnelles ou de scénarios prospectifs.",
        },
        D: {
          questions: [
            "Pouvez-vous me décrire une situation où vous avez pris une décision que vous avez ensuite regrettée ? Qu'avez-vous appris de cette expérience ?",
            "Comment réagissez-vous lorsque vous devez prendre une décision, mais que vous n'avez pas assez de temps pour analyser toutes les informations ?",
            "Si vous deviez prendre une décision rapidement dans un contexte inconnu, comment géreriez-vous le manque d'informations et d'expérience pour limiter les risques ?",
          ],
          analysis:
            "Ce talent a du mal à prendre des décisions cohérentes, se concentrant souvent sur des résultats immédiats au détriment d’une vision globale. Cela peut générer des erreurs ou des conséquences négatives à long terme.",
          advices:
            "Offrez un accompagnement régulier pour guider sa réflexion sur les implications des décisions. Introduisez des outils simples, comme des fiches de critères de choix, pour l’aider à structurer ses décisions.",
        },
        E: {
          questions: [
            "Pouvez-vous me parler d'un moment où vous avez eu du mal à prendre une décision importante ? Comment avez-vous essayé de surmonter cette difficulté ?",
            "Comment faites-vous face aux situations où vous ressentez de l'incertitude ou de la peur de prendre une mauvaise décision ?",
            "Si vous deviez prendre une décision sans expérience préalable et avec peu de temps pour y réfléchir, quelles étapes suivriez-vous pour essayer d’avancer malgré ces contraintes ?",
          ],
          analysis:
            "Ce talent montre des lacunes importantes dans la prise de décision, hésitant ou choisissant des actions inadaptées, particulièrement dans des situations à long terme. Cela peut entraîner une inefficacité opérationnelle ou un manque de crédibilité.",
          advices:
            "Mettez en place un encadrement structuré avec un mentor ou un manager pour l’aider à analyser les choix possibles. Introduisez des scénarios de décision simplifiés pour renforcer progressivement sa capacité à anticiper et structurer ses choix.",
        },
        skill_description:
          "La prise de décision consiste à évaluer des options et à choisir la plus appropriée pour atteindre un objectif, tout en anticipant les conséquences possibles.",
        advantages: [
          {
            icon: "sandglass",
            text: "Accélère les processus et réduit les pertes de temps.",
          },
          {
            icon: "card-people-plus",
            text: "Renforce la crédibilité dans des rôles stratégiques.",
          },
          {
            icon: "warning-triangle",
            text: "Améliore la gestion des crises et des situations complexes.",
          },
        ],
        short_description:
          "Analyse, jugement, détermination, la prise de décision permet d'agir de manière réfléchie et efficace.",
      },
    },
    {
      id: "empathy",
      type: "skill",
      name: "Empathie",
      result_wordings: {
        A: {
          questions: [
            "Pouvez-vous me parler d'une situation où vous avez aidé un collègue ou un client à surmonter un problème émotionnel ou professionnel en vous mettant à sa place ?",
            "Comment adaptez-vous votre communication pour montrer que vous comprenez vraiment les sentiments ou les préoccupations de vos collègues, surtout dans des situations difficiles ?",
            "Imaginez que vous devez gérer une équipe confrontée à une situation stressante. Comment montreriez-vous votre empathie tout en maintenant une dynamique de travail efficace ?",
          ],
          analysis:
            "Ce talent se distingue par sa capacité à comprendre les émotions et les besoins des autres, même dans des contextes complexes. Il établit facilement des liens de confiance, ce qui en fait un facilitateur naturel dans les environnements collaboratifs. Cette aptitude à anticiper les réactions de ses collègues renforce la cohésion d’équipe. Toutefois, une empathie trop marquée peut le rendre vulnérable à une surcharge émotionnelle, voire à des difficultés à poser des limites.",
          advices:
            "Orientez ce talent vers des projets collaboratifs ou des rôles de médiation où son empathie pourra être valorisée. Encouragez-le à équilibrer l’écoute des autres avec ses propres priorités pour éviter l’épuisement émotionnel.",
        },
        B: {
          questions: [
            "Pouvez-vous me donner un exemple d'une situation où vous avez pu apaiser une tension en comprenant les émotions et les points de vue de chacune des parties ?",
            "Comment démontrez-vous que vous prenez en compte non seulement les mots, mais aussi les émotions qui se cachent derrière ce que disent vos collègues ou clients ?",
            "Si un collègue était frustré par un projet, comment réagiriez-vous pour lui montrer que vous comprenez sa frustration tout en aidant à trouver une solution ?",
          ],
          analysis:
            "Ce talent manifeste une empathie solide, bien qu’il puisse encore progresser dans sa capacité à gérer des situations émotionnellement sensibles. Il reste capable d’adopter différents points de vue pour favoriser des échanges constructifs.",
          advices:
            "Proposez des missions où l’écoute et la coopération sont essentielles, tout en offrant des retours valorisants pour consolider ses compétences interpersonnelles. Incitez-le à développer son assertivité pour compléter sa sensibilité.",
        },
        C: {
          questions: [
            "Pouvez-vous me parler d'un moment où vous avez dû faire un effort pour comprendre les émotions d'un collègue dans une situation tendue ? Comment cela a-t-il influencé votre comportement ?",
            "Comment réagissez-vous lorsque quelqu'un exprime des émotions que vous avez du mal à comprendre ou à partager ?",
            "Si vous deviez travailler avec une personne qui se montre émotionnellement distante, comment essaieriez-vous de créer un lien empathique avec elle pour améliorer la collaboration ?",
          ],
          analysis:
            "Ce talent démontre une empathie partielle, parfois limitée par une attention insuffisante aux signaux émotionnels. Cette lacune peut restreindre son efficacité dans les échanges nécessitant une compréhension fine des dynamiques interpersonnelles.",
          advices:
            "Mettez-le dans des contextes collaboratifs avec des retours constructifs pour renforcer ses interactions.",
        },
        D: {
          questions: [
            "Pouvez-vous me décrire une situation où vous avez eu du mal à comprendre les émotions d’un collègue ou d’un client ? Comment avez-vous réagi à cela ?",
            "Comment gérez-vous les situations où vous devez rester objectif tout en étant confronté à des émotions fortes chez les autres ?",
            "Si quelqu'un vous reprochait de ne pas avoir pris en compte ses émotions dans une décision, comment aborderiez-vous cette critique pour améliorer la situation ?",
          ],
          analysis:
            "Ce talent rencontre des difficultés à comprendre et prendre en compte les émotions des autres. Cela peut nuire à sa capacité à collaborer efficacement et à désamorcer des tensions dans un groupe.",
          advices:
            "Proposez un accompagnement pour travailler sur la reconnaissance des émotions chez les autres. Intégrez-le à des missions d’équipe où des retours bienveillants pourront l’aider à progresser. Encouragez un apprentissage progressif à travers des mises en situation encadrées.",
        },
        E: {
          questions: [
            "Pouvez-vous me parler d'un moment où vous avez eu du mal à ressentir ou comprendre les émotions des autres dans un contexte professionnel ? Comment avez-vous réagi ?",
            "Comment faites-vous face aux situations où vous ressentez une distance émotionnelle avec vos collègues ou clients ?",
            "Si vous deviez traiter une situation où un membre de votre équipe montre des signes de stress ou de détresse, comment réagiriez-vous ?",
          ],
          analysis:
            "Ce talent montre une empathie très limitée, ce qui engendre des incompréhensions fréquentes et réduit sa capacité à s’intégrer harmonieusement dans une équipe. Un accompagnement est important pour développer sa sensibilité et améliorer sa relations aux autres.",
          advices:
            "Offrez des opportunités pour observer et développer l’empathie (par exemple, par des jeux de rôles ou des simulations d’équipe). Associez-le à un mentor expérimenté pour l’aider à mieux appréhender et répondre aux émotions de ses collègues.",
        },
        skill_description:
          "L’empathie désigne la capacité à comprendre et partager les émotions des autres tout en adaptant ses réponses en fonction des besoins perçus.",
        advantages: [
          {
            icon: "hand-care_icon_V2",
            text: "Renforce la cohésion et la confiance dans les équipes..",
          },
          {
            icon: "chat",
            text: "Favorise une communication plus fluide et harmonieuse.",
          },
          {
            icon: "rocket_icon_V2",
            text: "Améliore la résolution des conflits et l’efficacité des collaborations.",
          },
        ],
        short_description:
          "Compréhension émotionnelle, bienveillance, l'empathie permet de se connecter aux autres et de comprendre leurs perspectives.",
      },
    },
    {
      id: "planning",
      type: "skill",
      name: "Planification",
      disabled: false,
      result_wordings: {
        A: {
          questions: [
            "Pouvez-vous me parler d'un projet complexe où vous avez dû élaborer un plan détaillé pour atteindre les objectifs dans les délais ? Comment avez-vous géré les imprévus ?",
            "Comment intégrez-vous la flexibilité dans vos plans pour faire face aux changements ou aux obstacles inattendus ? Pouvez-vous donner un exemple concret ?",
            "Imaginez que vous devez planifier un projet avec plusieurs parties prenantes et des objectifs qui évoluent régulièrement. Comment structureriez-vous votre planification pour garder tout le monde aligné et éviter les retards ?",
          ],
          analysis:
            "Ce talent démontre une capacité exceptionnelle à planifier, anticipant avec précision les étapes nécessaires à la réussite d’un projet. Il excelle dans la définition de priorités claires et dans la gestion des ressources, ce qui optimise les performances de son équipe. Toutefois, une planification trop rigide pourrait limiter sa capacité à intégrer des ajustements en cours de projet.",
          advices:
            "Encouragez ce talent à partager ses méthodologies avec son équipe et à intégrer des marges de flexibilité dans ses plans. Orientez-le vers des projets complexes nécessitant une coordination à plusieurs niveaux pour maximiser son impact.",
        },
        B: {
          questions: [
            "Pouvez-vous me décrire une situation où une bonne planification vous a permis de mener un projet à bien malgré des défis majeurs ?",
            "Comment assurez-vous que votre plan reste réaliste tout en fixant des objectifs ambitieux ? Pouvez-vous me donner un exemple où cela a fonctionné efficacement ?",
            "Si vous deviez gérer un projet comportant des tâches interdépendantes, comment planifieriez-vous les différentes étapes pour éviter les goulets d'étranglement ?",
          ],
          analysis:
            "Ce talent planifie bien ses activités, bien qu’il puisse parfois manquer de précision dans des projets complexes. Il est capable de gérer les imprévus, mais un effort supplémentaire pourrait l’aider à améliorer sa réactivité dans des environnements dynamiques.",
          advices:
            "Proposez des outils (ex. diagrammes de Gantt ou analyse SWOT) ou méthodologies avancés (comme la méthode Agile) pour perfectionner ses capacités de planification dans des environnements complexes. Mettez-le en situation de coordonner des équipes variées.",
        },
        C: {
          questions: [
            "Pouvez-vous me parler d'un projet où vous avez dû revoir votre plan en cours de route ? Qu'est-ce qui a motivé ces ajustements, et comment les avez-vous gérés ?",
            "Comment priorisez-vous vos tâches lorsque vous avez plusieurs projets à gérer en même temps ?",
            "Si vous deviez planifier un projet avec des ressources limitées et des délais serrés, comment organiseriez-vous vos priorités pour maximiser l'efficacité ?",
          ],
          analysis:
            "Ce talent montre une capacité de planification moyenne, ayant parfois du mal à structurer efficacement ses tâches ou à anticiper les contraintes. Cela peut entraîner des retards ou une sous-utilisation des ressources.",
          advices:
            "Fournissez des exemples de bonnes pratiques et des outils comme des feuilles de route ou des check-lists pour guider ses processus. Fournissez des feedbacks réguliers pour renforcer ses bases en organisation.",
        },
        D: {
          questions: [
            "Pouvez-vous me décrire une situation où un manque de planification a entraîné des difficultés dans un projet ? Comment avez-vous réagi et que feriez-vous différemment aujourd'hui ?",
            "Comment gérez-vous les situations où votre plan initial ne se déroule pas comme prévu ?",
            "Si vous deviez travailler sur un projet qui nécessite une forte planification, mais que vous manquez de temps, comment tenteriez-vous d’établir un plan efficace malgré ces contraintes ?",
          ],
          analysis:
            "Ce talent rencontre des difficultés à organiser et prioriser ses actions, ce qui affecte directement la progression des projets. Il peut avoir tendance à être réactif plutôt que proactif, ce qui limite son efficacité.",
          advices:
            "Offrez un accompagnement individualisé axé sur la priorisation et l’utilisation d’outils de planification simples (comme Gantt ou Trello). Suivez régulièrement ses progrès dans la gestion de tâches complexes.",
        },
        E: {
          questions: [
            "Pouvez-vous me parler d’un moment où vous avez eu des difficultés à planifier un projet ? Comment avez-vous essayé de surmonter ces difficultés ?",
            "Comment faites-vous face à des situations où vous manquez de clarté sur la manière de structurer un projet ou une tâche importante ?",
            "Si vous deviez élaborer un plan pour un projet complexe sans expérience préalable, quelles étapes suivriez-vous pour essayer de structurer votre approche ?",
          ],
          analysis:
            "Ce talent présente des lacunes importantes en planification, manquant de structure dans son organisation. Cela peut générer des retards et une surcharge de travail imprévue pour ses collègues. ",
          advices:
            "Mettez en place un suivi avec des étapes de planification guidées. Associez-le à un mentor ou à un collègue expérimenté pour développer progressivement ses compétences. Valorisez ses progrès pour renforcer sa confiance et sa motivation.",
        },
        skill_description:
          "La planification consiste à organiser et structurer les étapes nécessaires pour atteindre un objectif, en optimisant les ressources et les délais.",
        advantages: [
          {
            icon: "document-tick",
            text: "Améliore la gestion des projets et des ressources.",
          },
          {
            icon: "calendar",
            text: "Réduit les risques de retards ou d’imprévus.",
          },
          {
            icon: "round-arrow-increase",
            text: "Accroît l’efficacité individuelle et collective.",
          },
        ],
        short_description:
          "Organisation, anticipation, structuration, la planification permet d'optimiser la réalisation des projets et objectifs.",
      },
    },
    {
      id: "time_manage",
      type: "skill",
      name: "Gestion du temps",
      result_wordings: {
        A: {
          questions: [
            "Pouvez-vous me parler d’un projet récent où vous avez dû gérer plusieurs tâches complexes en parallèle ? Comment avez-vous organisé votre temps pour respecter les délais ?",
            "Comment priorisez-vous les tâches lorsque vous avez plusieurs deadlines rapprochées ? Pouvez-vous donner un exemple concret ?",
            "Imaginez que vous travaillez sur un projet important avec des échéances très serrées. Comment organiseriez-vous votre emploi du temps pour garantir que chaque tâche soit effectuée efficacement sans compromettre la qualité du travail ?",
          ],
          analysis:
            "Ce talent excelle dans la gestion de son temps, planifiant et priorisant ses tâches avec une grande efficacité. Il optimise ses performances tout en respectant les délais, même dans des contextes exigeants. Sa rigueur inspire ses collègues et contribue à améliorer la productivité globale de son équipe. Cependant, une organisation trop rigide pourrait limiter sa flexibilité face aux imprévus.",
          advices:
            "Encouragez ce talent à partager ses bonnes pratiques avec l’équipe. Orientez-le vers des missions nécessitant une forte capacité de planification tout en cultivant une marge d’adaptabilité pour gérer les aléas.",
        },
        B: {
          questions: [
            "Pouvez-vous me décrire une situation où vous avez réussi à anticiper et à gérer efficacement une charge de travail imprévue ?",
            "Comment organisez-vous vos journées lorsque vous savez que plusieurs tâches risquent de vous prendre plus de temps que prévu ?",
            "Si vous deviez faire face à une tâche prioritaire inattendue qui modifie votre emploi du temps, comment ajusteriez-vous votre planification pour tenir compte de ce changement ?",
          ],
          analysis:
            "Ce talent gère bien son temps et respecte généralement les échéances. Cependant, il peut parfois manquer de flexibilité face à des imprévus. Avec une approche plus agile, il pourrait encore améliorer sa réactivité et son efficacité dans des situations dynamiques.",
          advices:
            "Proposez des formations sur la gestion agile des tâches pour l’aider à mieux s’adapter aux imprévus. Incitez-le à utiliser des outils numériques avancés pour renforcer sa planification.",
        },
        C: {
          questions: [
            "Pouvez-vous me parler d’une fois où vous avez dû ajuster votre emploi du temps parce que vous n’aviez pas prévu assez de temps pour une tâche importante ?",
            "Comment gérez-vous les situations où vous sentez que vous manquez de temps pour accomplir toutes vos tâches ?",
            "Si vous deviez jongler entre plusieurs projets, comment vous assureriez-vous de ne pas négliger les deadlines ?",
          ],
          analysis:
            "Ce talent montre une gestion du temps correcte, mais il est parfois dépassé par des priorités concurrentes ou des délais serrés. Une organisation plus structurée et l’utilisation d’outils de planification pourraient significativement renforcer son efficacité.",
          advices:
            "Fournissez-lui une formation sur la priorisation et les outils de planification (ex. matrices d’urgence, logiciels de gestion de projets). Offrez un accompagnement ponctuel pour l’aider à structurer ses tâches dans des contextes plus dynamiques.",
        },
        D: {
          questions: [
            "Pouvez-vous me décrire une situation où vous avez eu du mal à respecter un délai ? Quelles leçons en avez-vous tirées ?",
            "Comment réagissez-vous lorsque vous vous retrouvez en retard sur une tâche importante ? Quelles mesures prenez-vous pour rattraper le temps perdu ?",
            "Si vous deviez faire face à une surcharge de travail qui compromettrait vos deadlines, comment réorganiseriez-vous vos priorités pour minimiser les retards ?",
          ],
          analysis:
            "Ce talent a des difficultés à structurer ses journées, ce qui entraîne des retards et une baisse de la qualité de ses livrables. Il peut se retrouver submergé par des tâches multiples sans savoir lesquelles prioriser. Cela affecte directement son efficacité et son rôle dans les projets d’équipe.",
          advices:
            "Offrez un encadrement bienveillant avec des objectifs clairs et des check-ins réguliers pour l’aider à structurer son emploi du temps. Proposez des outils simples pour visualiser et prioriser ses tâches quotidiennes.",
        },
        E: {
          questions: [
            "Pouvez-vous me parler d'une fois où vous avez eu des difficultés à terminer une tâche dans les délais impartis ? Comment avez-vous géré cette situation ?",
            "Comment faites-vous face aux situations où vous vous sentez débordé par la gestion du temps ? Quelles stratégies utilisez-vous pour essayer de mieux gérer votre emploi du temps ?",
            "Si vous deviez travailler dans un environnement où la gestion du temps est cruciale et que vous n’aviez pas beaucoup de marge de manœuvre, comment aborderiez-vous cette contrainte pour améliorer votre efficacité ?",
          ],
          analysis:
            "Ce talent rencontre de graves lacunes en gestion du temps, ayant du mal à organiser ses tâches ou à respecter les échéances. Cela peut entraîner un stress personnel et nuire à la dynamique d’équipe. Il est important de l’accompagner sur ce sujet.",
          advices:
            "Mettez en place un suivi avec un mentor ou un manager pour l’accompagner dans l’apprentissage des fondamentaux de la gestion du temps. Favorisez des objectifs atteignables pour construire progressivement sa discipline et sa confiance.",
        },
        skill_description:
          "La gestion du temps consiste à organiser, prioriser et planifier ses tâches pour atteindre ses objectifs dans les délais impartis.",
        advantages: [
          {
            icon: "round-arrow-increase",
            text: "Augmente la productivité individuelle et collective.",
          },
          {
            icon: "chart-decrease",
            text: "Réduit le stress lié aux échéances et aux imprévus.",
          },
          {
            icon: "phone-out",
            text: "Favorise une meilleure coordination au sein de l’équipe.",
          },
        ],
        short_description:
          "Organisation, priorisation, efficacité, la gestion du temps permet d'optimiser sa productivité.",
      },
    },
    {
      id: "communication",
      type: "style",
      name: "Communication",
      disabled: true,
    },
    {
      id: "leadership",
      type: "style",
      name: "Leadership",
      disabled: false,
      result_wordings: {
        transformational: {
          id: "transformational",
          label: "Mobilisateur",
          analysis: "Ce talent inspire et motive les collaborateurs en communiquant une vision claire, en stimulant l’innovation et en répondant aux besoins individuels.",
          analysis_communication: "Ce style repose sur un langage inspirant et symbolique. Les leaders transformationnels utilisent des métaphores, des récits et des messages motivants pour mobiliser leurs équipes. Ils donnent également un feedback personnalisé et constructif, en tenant compte des besoins spécifiques de chaque individu.",
          analysis_teamwork: "Ce style favorise une forte cohésion grâce à une vision partagée. Les collaborateurs se sentent valorisés et motivés à collaborer pour atteindre des objectifs communs. Le leader transformationnel stimule également la créativité collective en encourageant les échanges d’idées et en créant un climat propice à l’innovation.",
          plus: [
            {
              icon: "rocket_icon_V2",
              text: "Motive et engage fortement les collaborateurs.",
            },
            {
              icon: "bulb_icon_V2",
              text: "Favorise l’innovation et la créativité.",
            }
          ],
          minus: [
            {
              icon: "thunder_icon_V2",
              text: "Exigeant pour le leader, demandant beaucoup de charisme et d’énergie.",
            },
            {
              icon: "hand-care_icon_V2",
              text: "Peut créer une dépendance au leader.",
            }
          ]
        },
        transactional: {
          id: "transactional",
          label: "Pragmatique",
          analysis: "Ce talent se base sur des échanges clairs avec les collaborateurs : objectifs mesurables, récompenses ou absence de récompense.",
          analysis_communication: "Le leader transactionnel adopte une approche directive, basée sur des attentes précises et des évaluations régulières. La communication est orientée vers les résultats et peut parfois être perçue comme impersonnelle, car elle se limite souvent à des aspects fonctionnels.",
          analysis_teamwork: "Ce style peut structurer efficacement les efforts collectifs en clarifiant les rôles et les responsabilités. Cependant, il favorise davantage l’atteinte des objectifs individuels que l’esprit de collaboration. Cela peut générer une compétition interne au sein de l’équipe, au détriment de la cohésion.",
          plus: [
            {
              icon: "target_icon_V2",
              text: "Structure et clarifie les attentes et les performances.",
            },
            {
              icon: "switch_icon_V2",
              text: "Convient bien pour des tâches répétitives et standardisées.",
            }
          ],
          minus: [
            {
              icon: "bulb_icon_V2",
              text: "Limite la créativité et l’innovation.",
            },
            {
              icon: "heart-hexagon_icon_V2",
              text: "Les collaborateurs peuvent se sentir peu impliqués émotionnellement.",
            }
          ]
        },
        laissez_faire: {
          id: "laissez_faire",
          label: "Laissez-faire",
          analysis: "Ce talent se caractérise par une absence ou une faible intervention, qui laisse les collaborateurs gérer leurs tâches de manière autonome.",
          analysis_communication: "Ce style est marqué par une absence ou un faible niveau d’échanges. Le leader laisse les collaborateurs s’organiser eux-mêmes, ce qui peut entraîner un manque de feedback et de clarté sur les attentes. Cela peut générer des frustrations ou des malentendus au sein des équipes.",
          analysis_teamwork: "Le manque de supervision peut entraîner une désorganisation ou un déficit de coordination, surtout si les collaborateurs manquent d’expérience. Cependant, dans des environnements où l’équipe est très compétente et autonome, ce style peut encourager l’initiative individuelle et l’auto-organisation.",
          plus: [
            {
              icon: "switch_icon_V2",
              text: "Encourage une forte autonomie.",
            },
            {
              icon: "medal_icon_V2",
              text: "Fonctionne bien avec des équipes expérimentées et autonomes.",
            }
          ],
          minus: [
            {
              icon: "document-search__icon_V2",
              text: "Manque de supervision et de feedback.",
            },
            {
              icon: "path_icon_V2",
              text: "Peut entraîner une confusion ou un manque de coordination.",
            }
          ]
        },
        // Used when all styles have a score of 0
        unknown: {
          id: "unknown",
          label: "Profil non déterminé",
          analysis: "Aucun style dominant n’émerge dans les réponses. Le candidat n’exprime pas de comportements typiques de leadership dans ce test.",
          analysis_communication: "Aucune conclusion certaine ne peut être tirée sur la posture communicationnelle du candidat à partir de ce résultat.",
          analysis_teamwork: "Aucune conclusion certaine ne peut être tirée sur la posture du candidat dans les dynamiques de groupe à partir de ce résultat.",
          plus: "N/A",
          minus: "N/A",
        },
        skill_description:
          "Le leadership représente la capacité à inspirer, guider et mobiliser une équipe pour atteindre des objectifs communs, même dans des contextes exigeants.",
        advantages: [
          {
            icon: "network",
            text: "Accroît l'engagement et la motivation des équipes.",
          },
          {
            icon: "laptop_icon_V2",
            text: "Facilite la mise en œuvre d'une vision stratégique.",
          },
          {
            icon: "heart-hexagon_icon_V2",
            text: "Améliore la gestion des conflits et des situations critiques.",
          },
        ],
      },
    },
    {
      id: "team_work",
      type: "style",
      name: "Travail en équipe",
      disabled: true,
    },
    {
      id: "learn_strategy",
      type: "style",
      name: "Stratégie d'apprentissage",
      result_wordings: {
        A: {
          analysis:
            "Ce talent apprend en répétant ou relisant mécaniquement les informations, tout en se concentrant sur la mémorisation des contenus.",
          label: "Répétition",
          id: "Repeating",
          plus: "Excellente capacité à mémoriser rapidement des informations simples ou factuelles. Idéal pour les tâches nécessitant des connaissances précises.",
          minus:
            "Limité dans la compréhension profonde ou l’application des concepts. Peu efficace face à des tâches nécessitant une analyse critique pour mémoriser des connaissances à long terme",
          questions: [
            "Pouvez-vous me parler d’une situation où vous avez dû apprendre une nouvelle compétence en répétant régulièrement certaines actions ? Comment cela a-t-il fonctionné pour vous ?",
            "Comment utilisez-vous la répétition dans votre processus d’apprentissage, et comment vous assurez-vous que cela reste efficace sans devenir mécanique ?",
            "Avez-vous déjà rencontré une situation où la simple répétition ne suffisait pas pour maîtriser une compétence ? Comment avez-vous ajusté votre approche ?",
          ],
          advices:
            "Proposez des supports d’apprentissage basés sur des exercices pratiques et la répétition espacée. Favorisez l’entraînement régulier par des mises en situation pour ancrer durablement les connaissances.",
        },
        B: {
          analysis:
            "Ce talent associe les nouvelles informations à des connaissances préexistantes, utilisant des exemples ou des analogies pour enrichir sa compréhension.",
          label: "Élaboration",
          id: "Reformulate",
          plus: "Permet une compréhension approfondie et durable des contenus. Idéal pour intégrer des concepts complexes ou interdisciplinaires.",
          minus:
            "Peut être chronophage pour des tâches simples. Risque de suranalyser des informations basiques.",
          questions: [
            "Pouvez-vous me donner un exemple où vous avez approfondi un sujet en créant des liens avec d'autres connaissances ? Comment cela a-t-il renforcé votre apprentissage ?",
            "Comment adaptez-vous vos méthodes d’apprentissage pour intégrer de nouvelles informations de manière plus approfondie et durable ?",
            "Lorsque vous apprenez quelque chose de complexe, comment le reformulez-vous ou l'expliquez-vous pour mieux l’intégrer ?",
          ],
          advices:
            "Encouragez ce talent à approfondir les sujets en reliant de nouvelles connaissances à celles déjà acquises. Proposez des discussions de groupe ou des études de cas pour favoriser une compréhension approfondie.",
        },
        C: {
          analysis:
            "Ce talent structure les informations en catégorisant, représentant et hiérarchisant les idées pour faciliter leur assimilation.",
          label: "Organisation",
          id: "Organisation",
          plus: "Aide à établir des connexions claires entre les concepts. Utile pour organiser des projets ou des tâches complexes.",
          minus:
            "Peut être rigide et limiter la flexibilité face à des situations imprévues. Moins adapté aux apprentissages non structurés.",
          questions: [
            "Pouvez-vous partager un exemple où vous avez structuré des informations complexes pour faciliter votre apprentissage ou celui des autres ?",
            "Comment planifiez-vous votre apprentissage ou la gestion de vos tâches pour maximiser votre efficacité ?",
            "Quand vous devez gérer plusieurs projets simultanément, quelles stratégies utilisez-vous pour organiser votre temps et vos priorités ?",
          ],
          advices:
            "Fournissez des outils et des méthodologies de structuration (ex. tableaux, mind maps, logiciels de gestion de projet). Aidez-le à hiérarchiser les informations et à établir des plans clairs pour optimiser son apprentissage.",
        },
        D: {},
        E: {
          analysis:
            "Ce talent remet en question les informations, analyse leur validité et cherche à comprendre les fondements sous-jacents.",
          label: "Pensée critique",
          id: "Thinking",
          plus: "Excellente capacité à résoudre des problèmes complexes et à évaluer des concepts avec discernement. Favorise l’innovation et les solutions créatives.",
          minus:
            "Peut ralentir l’apprentissage en raison d’un questionnement excessif. Risque de suranalyser des informations non critiques.",
          questions: [
            "Pouvez-vous me parler d'une situation où vous avez dû remettre en question une information ou une méthodologie avant de l'accepter ?",
            "Comment évaluez-vous la fiabilité et la pertinence des informations que vous utilisez dans votre travail ?",
            "Face à un problème complexe, quelles démarches adoptez-vous pour analyser les différentes perspectives et prendre une décision éclairée ?",
          ],
          advices:
            "Incitez-le à questionner les sources d’information et à analyser différentes perspectives avant de tirer des conclusions. Mettez à sa disposition des ressources variées et stimulez son esprit d’analyse avec des débats et des études comparatives.",
        },
        skill_description:
          "La stratégie d’apprentissage désigne les mécanismes utilisés par le talent afin d’apprendre de nouvelles informations et développer ses compétences.",
        advantages: [
          {
            icon: "round-arrow-increase",
            text: "Comprendre ses stratégies et les faire évoluer",
          },
          {
            icon: "chart-decrease",
            text: "Éprouver de nouvelles stratégies plus efficaces",
          },
          {
            icon: "phone-out",
            text: "Favoriser un apprentissage efficace au sein de l’entreprise.",
          },
        ],
      },
    },
    {
      id: "motivation",
      type: "style",
      name: "Motivation",
      disabled: false,
      result_wordings: {
        Intra: {
          label: "Buts intrinsèques",
          analysis: "Ce talent est motivé par le désir d'apprendre et de se développer personnellement, cherchant la satisfaction dans la maîtrise des compétences.",
          questions: [
            "Pouvez-vous me donner un exemple de projet ou de tâche que vous avez accompli(e) simplement par passion ou intérêt personnel ? Comment cela a-t-il influencé votre engagement et votre performance ?",
            "Comment maintenez-vous votre motivation lorsque vous travaillez sur un projet qui ne présente pas d’avantages immédiats ou tangibles ?",
            "Lorsque vous êtes confronté à des difficultés, qu'est-ce qui vous pousse à persévérer, au-delà des récompenses externes ?",
          ],
          plus: "Grande autonomie et persévérance face aux défis. Idéal pour les projets nécessitant créativité et innovation.",
          minus: "Risque de s'écarter des priorités opérationnelles.",
        },
        Extra: {
          label: "Buts extrinsèques",
          analysis: "Ce talent est motivé par les récompenses externes, la reconnaissance et l'atteinte d'objectifs mesurables fixés par l'organisation.",
          questions: [
            "Pouvez-vous me parler d'une situation où vous avez été particulièrement motivé(e) par une récompense ou une reconnaissance externe ? Comment cela a-t-il affecté votre travail ?",
            "Comment gérez-vous votre engagement lorsque la récompense finale est incertaine ou éloignée dans le temps ?",
            "Quels types d’incitations ou de feedbacks extérieurs ont le plus d’impact sur votre motivation et votre engagement ?",
          ],
          plus: "Excellente capacité à atteindre des objectifs quantifiables. Particulièrement efficace dans les environnements orientés performance.",
          minus:
            "Peut perdre en motivation en l'absence de récompenses visibles. Tendance à privilégier les résultats rapides au détriment de la qualité.",
        },
        Values: {
          label: "Valeur de la tâche",
          analysis: "Ce talent évalue et s'engage dans les activités en fonction de leur utilité perçue et de leur alignement avec ses objectifs personnels",
          questions: [
            "Pouvez-vous me donner un exemple où vous avez pris en charge une tâche parce que vous en perceviez l'importance pour l'organisation ou pour votre propre développement ?",
            "Comment évaluez-vous l’importance d’une tâche par rapport à vos objectifs personnels et professionnels ?",
            "Que faites-vous lorsqu'une tâche semble peu engageante ou dénuée de sens ? Comment trouvez-vous de la valeur dans votre travail ?",
          ],
          plus: "Forte implication dans les projets jugés pertinents. Contribue efficacement aux initiatives stratégiques.",
          minus:
            "Peut montrer un désengagement face aux tâches perçues comme moins importantes.",
        },
        Feeling: {
          label: "Auto-efficacité",
          analysis: "Ce talent base sa motivation sur sa confiance en ses capacités à réussir et à surmonter les obstacles rencontrés.",
          questions: [
            "Pouvez-vous partager un moment où vous avez affronté un défi ambitieux et où vous étiez convaincu(e) de votre capacité à réussir ?",
            "Comment réagissez-vous lorsque vous vous sentez incertain(e) quant à vos compétences sur une tâche nouvelle ou difficile ?",
            "Quels éléments influencent votre confiance en votre capacité à accomplir une tâche ?",
          ],
          plus: "Grande résilience face aux défis et capacité à maintenir l'effort dans la durée. Favorise la prise d'initiative.",
          minus: "Risque de découragement face aux échecs inattendus.",
        },
      }
    }
  ]);

  return { modules };
});
